import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Button,
  Stack,
  Chip
} from '@mui/material';
import projectService from '../../services/projectService';
import { useAuth } from '../../contexts/AuthContext';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import ProjectDialog from './ProjectDialog';

const ProjectSelector = ({
  value = null,
  onChange,
  error = false,
  helperText = '',
  disabled = false,
  currentMeetingId = null
}) => {
  const { currentUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(value);

  useEffect(() => {
    if (currentUser && currentUser.uid) {
      loadProjects();
    }
  }, [currentUser]);

  useEffect(() => {
    if (value && value.id && projects.length > 0) {
      const fullProject = projects.find(p => p.id === value.id);
      if (fullProject) {
        setSelectedProject(fullProject);
      } else {
        setSelectedProject(value);
      }
    } else {
      setSelectedProject(value);
    }
  }, [value, projects]);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const userProjects = await projectService.getUserProjects(currentUser.uid);
      setProjects(userProjects);
    } catch (error) {
      console.error('[ProjectSelector] ❌ Erreur chargement projets:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (event, newValue) => {
    if (newValue && newValue.isNewProject) {
      setDialogOpen(true);
      return;
    }

    setSelectedProject(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleProjectCreated = async (newProject) => {
    await loadProjects();
    setSelectedProject(newProject);
    if (onChange) {
      onChange(newProject);
    }
    setDialogOpen(false);
  };

  const options = [
    { id: 'new', name: '➕ Créer un nouveau projet', isNewProject: true },
    ...projects
  ];

  return (
    <>
      <Autocomplete
        value={selectedProject}
        onChange={handleProjectChange}
        options={options}
        getOptionLabel={(option) => option?.name || ''}
        loading={loading}
        disabled={disabled}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Projet"
            placeholder="Sélectionnez ou créez un projet"
            error={error}
            helperText={helperText || 'Optionnel : regroupez vos réunions par projet'}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
        renderOption={(props, option) => {
          // Extraire la key des props pour éviter le warning React
          const { key, ...otherProps } = props;

          if (option.isNewProject) {
            return (
              <li key={key} {...otherProps}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <AddIcon color="primary" />
                  <Typography color="primary" fontWeight="medium">
                    {option.name}
                  </Typography>
                </Stack>
              </li>
            );
          }

          return (
            <li key={key} {...otherProps}>
              <Stack direction="row" spacing={2} alignItems="center" width="100%">
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: option.color || '#1976d2',
                    flexShrink: 0
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap>{option.name}</Typography>
                  {option.description && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {option.description}
                    </Typography>
                  )}
                </Box>
                {option.meetingIds && option.meetingIds.length > 0 && (
                  <Chip
                    size="small"
                    label={`${option.meetingIds.length} réunion${option.meetingIds.length > 1 ? 's' : ''}`}
                    sx={{ flexShrink: 0 }}
                  />
                )}
              </Stack>
            </li>
          );
        }}
        noOptionsText={loading ? "Chargement..." : "Aucun projet"}
      />

      <ProjectDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={handleProjectCreated}
        initialMeetingId={currentMeetingId}
      />
    </>
  );
};

export default ProjectSelector;
